import type { Breadcrumb, ErrorEvent, SeverityLevel, StackFrame } from "@sentry/browser";
// Not re-exported from @sentry/browser.
import type { BaseTransportOptions, Envelope } from "@sentry/core";

export const WARN = "WARN";
export const ERROR = "ERROR";

export type Level = typeof WARN | typeof ERROR;

export const ERROR_IMPACT = {
	APP_CRASH: "app_crash",
	FLOW_FAILURE: "flow_failure",
	NON_BLOCKING: "non_blocking",
	UNKNOWN: "unknown",
} as const;

export type FailureContext = {
	impact: typeof ERROR_IMPACT.APP_CRASH | typeof ERROR_IMPACT.FLOW_FAILURE;
	source: string;
	operation?: string;
	runtimeContext?: string;
	componentStack?: string | null;
	// Event body text, never a tag; no user content or secrets.
	detail?: string;
	// Replaces the default stack-based grouping.
	fingerprint?: string[];
};

export const ENVIRONMENT = {
	PROD: "prod",
	STAGE: "stage",
	TEST: "test",
	EU: "eu",
	DEV: "dev",
} as const;

export type Environment = typeof ENVIRONMENT[keyof typeof ENVIRONMENT];

export const HOST_FRAGMENT = {
	TEST: "test",
	STAGE: "stage",
	EU: "eu.wevideo",
	DEV: "dev.wevideo",
} as const;

// First matching host fragment wins; a host matching none is production.
export const HOST_ENVIRONMENTS: readonly (readonly [string, Environment])[] = [
	[HOST_FRAGMENT.STAGE, ENVIRONMENT.STAGE],
	[HOST_FRAGMENT.TEST, ENVIRONMENT.TEST],
	[HOST_FRAGMENT.DEV, ENVIRONMENT.DEV],
	[HOST_FRAGMENT.EU, ENVIRONMENT.EU],
];

export const PRODUCTION_ENVIRONMENTS: readonly string[] = [ENVIRONMENT.PROD, ENVIRONMENT.EU];

export const TAG = {
	APP_NAME: "appName",
	CODE_SOURCE: "trackjs_code_source",
	PAGE_URL: "page_url",
	IS_EXTERNAL: "trackjs_is_external",
	IS_VENDOR: "trackjs_is_vendor",
	LEVEL: "trackjs_level",
	INSTANCE_NAME: "instanceName",
	SESSION_ID: "sessionId",
	TRACKER: "error_tracker",
	IMPACT: "error_impact",
	SOURCE: "error_source",
	OPERATION: "error_operation",
	RUNTIME_CONTEXT: "runtime_context",
	PROBE: "error_probe",
	FAILED_SCRIPT: "failed_script",
	FAILED_CHUNK: "failed_chunk",
	FAILED_CHUNK_STATUS: "failed_chunk_status",
	FAILED_CHUNK_DURATION: "failed_chunk_ms",
	FAILED_CHUNK_SIZE: "failed_chunk_bytes",
	CONNECTION_TYPE: "connection_type",
	AUTO_RELOAD: "auto_reload",
	RESOURCE_CHECK: "resource_check",
	FIRST_FAILED_CHUNK: "first_failed_chunk",
	FIRST_FAILED_CHUNK_STATUS: "first_failed_chunk_status",
	FIRST_FAILED_CHUNK_DURATION: "first_failed_chunk_ms",
	FIRST_CONNECTION_TYPE: "first_connection_type",
	STORAGE_UNAVAILABLE: "storage_unavailable",
} as const;

export const TRACKER_SENTRY = "sentry";

export const TEST_CRASH_EVENT = "wevideo:test-crash";
export const TEST_ROUTE_CRASH_EVENT = "wevideo:test-route-crash";
export const TEST_ERROR_SOURCE = "test_shortcut";
export const TEST_REQUEST_PATH = "/api/5/test-error-shortcut";
// URL parameter that triggers a startup probe outside production.
export const TEST_CRASH_PARAM = "testCrash";

export const APP_VERSION_HEADER = "wev-app-version";
export const MAX_DETAIL_LENGTH = 4000;

export const SENTRY_LEVEL: Record<Level, SeverityLevel> = {
	[WARN]: "warning",
	[ERROR]: "error",
};

export type StackFrameClassifier = (frame: StackFrame) => boolean;

export type ErrorTrackingUser = {
	id: string | number;
	instanceName?: string;
	sessionId?: string;
};

// The SDK only enables itself and builds the transport when a syntactically
// valid DSN is present, but the real DSN is applied by the backend when it
// forwards the envelope. This placeholder satisfies the SDK without exposing it.
export const DEFAULT_DSN = "https://placeholder@sentry-placeholder.invalid/0";

export const DEFAULT_ENDPOINT = "/api/5/error-tracking";

export type ErrorTrackingConfig = {
	// When omitted, the shared app name (`api/appInfo`) is resolved per event,
	// picking up `setAppName` calls made after init. An explicit value stays fixed.
	appName?: string;
	// Falls back to the build version from `api/appInfo` when omitted.
	appVersion?: string;
	dsn?: string;
	// A getter is resolved per-send, for endpoints not known at init time; a plain
	// string is used as-is.
	endpoint?: string | (() => string);
	environment?: string;
	release?: string;
	// Per-send auth headers (e.g. anonymous / session tokens).
	getAuthHeaders?: () => Record<string, string>;
	ignoreMessages?: (string | RegExp)[];
	// Messages dropped entirely (not just downgraded to WARN), in addition to the
	// built-in spam list. Symmetric with `spamStackFragments` but matched on the
	// error message instead of the stack.
	spamMessages?: (string | RegExp)[];
	spamStackFragments?: (string | RegExp)[];
	isInternalFrame?: StackFrameClassifier;
	isVendorFrame?: StackFrameClassifier;
	// Drop errors whose stack contains no internal frame outright, instead of the
	// default behaviour of downgrading them to WARN and still reporting them. Useful
	// for contexts (e.g. a browser extension) where third-party page code is pure noise.
	dropExternal?: boolean;
	beforeSend?: (event: ErrorEvent) => ErrorEvent | null;
	// Runs after the built-in breadcrumb generalization; return null to drop the
	// breadcrumb. Lets consumers filter (e.g. asset/data-URL breadcrumbs) or reroute.
	beforeBreadcrumb?: (breadcrumb: Breadcrumb) => Breadcrumb | null;
	// Wrap the transport in Sentry's offline transport so envelopes are persisted to
	// IndexedDB and retried when the context comes back online / is resumed. Required
	// for MV3 service workers, which can be suspended between an error and its send.
	offline?: boolean;
	// Passed straight to `Sentry.init`'s `transportOptions`; carries offline-transport
	// settings (dbName, storeName, maxQueueSize, flushAtStartup, shouldStore).
	transportOptions?: ErrorTransportOptions;
};

// Sentry doesn't export its browser/offline transport option types from a stable
// entrypoint, so model the subset we pass to `Sentry.init`'s `transportOptions` on the
// public `BaseTransportOptions`. The offline-* fields are read by the offline transport.
export type ErrorTransportOptions = BaseTransportOptions & {
	dbName?: string;
	storeName?: string;
	maxQueueSize?: number;
	flushAtStartup?: boolean;
	shouldStore?: (envelope: Envelope, error: Error, retryDelay: number) => boolean;
};
